import { Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatIDR } from '../lib/format';

export interface LineDraft {
  description: string;
  quantity: number;
  unitPrice: number;
}

export function emptyLine(): LineDraft {
  return { description: '', quantity: 1, unitPrice: 0 };
}

/** Editable list of {description, quantity, unitPrice} rows with a live subtotal. */
export function LineItemsEditor({
  lines,
  onChange,
}: {
  lines: LineDraft[];
  onChange: (lines: LineDraft[]) => void;
}) {
  const update = (i: number, patch: Partial<LineDraft>) =>
    onChange(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const remove = (i: number) => onChange(lines.filter((_, idx) => idx !== i));
  const subtotal = lines.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0);

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2">Line items</Typography>
      {lines.map((line, i) => (
        <Stack direction="row" gap={1} key={i} alignItems="center">
          <TextField
            label="Description"
            value={line.description}
            onChange={(e) => update(i, { description: e.target.value })}
            sx={{ flex: 1 }}
            size="small"
          />
          <TextField
            label="Qty"
            type="number"
            value={line.quantity}
            onChange={(e) => update(i, { quantity: Number(e.target.value) })}
            sx={{ width: 90 }}
            size="small"
          />
          <TextField
            label="Unit price"
            type="number"
            value={line.unitPrice}
            onChange={(e) => update(i, { unitPrice: Number(e.target.value) })}
            sx={{ width: 150 }}
            size="small"
          />
          <Typography variant="body2" sx={{ width: 130, textAlign: 'right' }}>
            {formatIDR(line.quantity * line.unitPrice)}
          </Typography>
          <IconButton
            size="small"
            onClick={() => remove(i)}
            disabled={lines.length === 1}
            aria-label="remove line"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...lines, emptyLine()])}>
          Add line
        </Button>
        <Typography variant="subtitle2">Subtotal (DPP): {formatIDR(subtotal)}</Typography>
      </Stack>
    </Stack>
  );
}
